import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCourseContent, completeLesson, submitQuiz } from '../utils/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle, Circle, Play, FileText, HelpCircle, ChevronLeft,
  ChevronRight, Menu, X, BookOpen, Award, ArrowLeft, Clock
} from 'lucide-react';

// ── helpers ────────────────────────────────────────────────────────────────────
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^?&\n]+)/);
  return m?.[1] || null;
}

function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m?.[1] || null;
}

// ── sub-components ─────────────────────────────────────────────────────────────
const TypeIcon = ({ type, size = 'w-4 h-4' }) => {
  if (type === 'video') return <Play className={`${size} text-blue-500`} />;
  if (type === 'quiz') return <HelpCircle className={`${size} text-amber-500`} />;
  return <FileText className={`${size} text-green-500`} />;
};

const VideoPlayer = ({ url }) => {
  const ytId = getYouTubeId(url);
  const vimeoId = getVimeoId(url);
  if (ytId) {
    return (
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          className="absolute inset-0 w-full h-full rounded-lg"
          src={`https://www.youtube.com/embed/${ytId}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Video de la lección"
        />
      </div>
    );
  }
  if (vimeoId) {
    return (
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          className="absolute inset-0 w-full h-full rounded-lg"
          src={`https://player.vimeo.com/video/${vimeoId}`}
          allowFullScreen
          title="Video de la lección"
        />
      </div>
    );
  }
  return (
    <video src={url} controls className="w-full rounded-lg max-h-96 bg-black">
      Tu navegador no soporta el elemento de video.
    </video>
  );
};

const QuizPlayer = ({ lesson, courseId, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const questions = lesson.quiz_questions || [];

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error('Debes responder todas las preguntas');
      return;
    }
    setSubmitting(true);
    try {
      const answerArr = questions.map((_, i) => answers[i] ?? -1);
      const res = await submitQuiz({ course_id: courseId, lesson_id: lesson.id, answers: answerArr });
      setResult(res.data);
      if (res.data.passed) {
        toast.success(`¡Aprobaste! Puntuación: ${res.data.score}%`);
        onComplete(res.data.score, res.data.progress);
      } else {
        toast.error(`Puntuación: ${res.data.score}%. Necesitas al menos 60% para aprobar.`);
      }
    } catch {
      toast.error('Error al enviar el quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setResult(null);
  };

  if (questions.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground border rounded-lg">
        <HelpCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>Este quiz no tiene preguntas configuradas aún.</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className={`p-6 rounded-xl border-2 text-center ${result.passed ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="quiz-result">
        <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${result.passed ? 'bg-green-500' : 'bg-red-400'}`}>
          {result.passed ? <Award className="w-8 h-8 text-white" /> : <X className="w-8 h-8 text-white" />}
        </div>
        <h3 className={`text-2xl font-bold mb-2 ${result.passed ? 'text-green-700' : 'text-red-600'}`}>
          {result.passed ? '¡Aprobado!' : 'No aprobado'}
        </h3>
        <p className="text-3xl font-black mb-1">{result.score}%</p>
        <p className="text-muted-foreground mb-4">
          {result.correct} de {result.total} respuestas correctas
        </p>
        {!result.passed && (
          <Button onClick={handleRetry} variant="outline" data-testid="quiz-retry-btn">
            Intentar de nuevo
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quiz-player">
      {questions.map((q, qi) => (
        <div key={qi} className="p-5 border rounded-xl bg-background" data-testid={`question-${qi}`}>
          <p className="font-semibold mb-4 text-base">{qi + 1}. {q.question}</p>
          <div className="space-y-2">
            {(q.options || []).map((opt, oi) => (
              <label
                key={oi}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  answers[qi] === oi
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-muted/30'
                }`}
              >
                <input
                  type="radio"
                  name={`q-${qi}`}
                  value={oi}
                  checked={answers[qi] === oi}
                  onChange={() => setAnswers(prev => ({ ...prev, [qi]: oi }))}
                  className="accent-primary"
                  data-testid={`option-${qi}-${oi}`}
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <Button
        onClick={handleSubmit}
        disabled={submitting || Object.keys(answers).length < questions.length}
        className="w-full"
        size="lg"
        data-testid="quiz-submit-btn"
      >
        {submitting ? 'Enviando...' : 'Enviar respuestas'}
      </Button>
    </div>
  );
};

// ── sidebar ────────────────────────────────────────────────────────────────────
const CourseSidebar = ({ content, activeLessonId, onSelect, isOpen, onClose }) => (
  <aside
    className={`fixed inset-y-0 left-0 z-30 w-72 bg-background border-r flex flex-col transform transition-transform duration-300
      md:relative md:translate-x-0 md:flex
      ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
    data-testid="course-sidebar"
  >
    {/* Header */}
    <div className="p-4 border-b bg-muted/30 shrink-0">
      <div className="flex items-center justify-between mb-3">
        <Link to={`/courses/${content.course.id}`}>
          <Button variant="ghost" size="sm" className="gap-1 text-xs -ml-2">
            <ArrowLeft className="w-3 h-3" />
            Al curso
          </Button>
        </Link>
        <button onClick={onClose} className="md:hidden p-1 rounded hover:bg-muted">
          <X className="w-5 h-5" />
        </button>
      </div>
      <h2 className="font-semibold text-sm line-clamp-2 mb-2">{content.course.title}</h2>
      {!content.is_instructor && (
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{content.completed_lessons}/{content.total_lessons} lecciones</span>
            <span>{content.progress}%</span>
          </div>
          <Progress value={content.progress} className="h-1.5" />
        </div>
      )}
    </div>

    {/* Modules & lessons */}
    <div className="flex-1 overflow-y-auto py-2">
      {content.modules.map((mod, mi) => (
        <div key={mod.id}>
          <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-muted-foreground bg-muted/20">
            Módulo {mi + 1}: {mod.title}
          </div>
          {mod.lessons.map((lesson) => (
            <button
              key={lesson.id}
              onClick={() => { onSelect(lesson.id); onClose(); }}
              data-testid={`sidebar-lesson-${lesson.id}`}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors
                ${activeLessonId === lesson.id
                  ? 'bg-primary/10 text-primary border-r-2 border-primary'
                  : 'hover:bg-muted/40 text-foreground'}`}
            >
              <span className="shrink-0">
                {lesson.is_completed
                  ? <CheckCircle className="w-4 h-4 text-green-500" />
                  : <Circle className="w-4 h-4 text-muted-foreground/40" />}
              </span>
              <TypeIcon type={lesson.type} />
              <span className="flex-1 line-clamp-2 text-xs">{lesson.title}</span>
              {lesson.duration_minutes && (
                <span className="text-xs text-muted-foreground shrink-0">{lesson.duration_minutes}m</span>
              )}
            </button>
          ))}
        </div>
      ))}
      {content.modules.length === 0 && (
        <div className="p-6 text-center text-muted-foreground text-sm">
          <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p>Este curso aún no tiene contenido</p>
        </div>
      )}
    </div>
  </aside>
);

// ── main component ─────────────────────────────────────────────────────────────
const CourseLearn = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [content, setContent] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const allLessons = useMemo(
    () => content?.modules.flatMap(m => m.lessons) || [],
    [content]
  );
  const activeLesson = useMemo(
    () => allLessons.find(l => l.id === activeLessonId),
    [allLessons, activeLessonId]
  );
  const lessonIndex = allLessons.findIndex(l => l.id === activeLessonId);
  const prevLesson = lessonIndex > 0 ? allLessons[lessonIndex - 1] : null;
  const nextLesson = lessonIndex < allLessons.length - 1 ? allLessons[lessonIndex + 1] : null;

  const fetchContent = useCallback(async () => {
    try {
      const res = await getCourseContent(id);
      setContent(res.data);
      const allL = res.data.modules.flatMap(m => m.lessons);
      const firstIncomplete = allL.find(l => !l.is_completed) || allL[0];
      if (firstIncomplete) setActiveLessonId(firstIncomplete.id);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('No estás inscrito en este curso');
        navigate(`/courses/${id}`);
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Error al cargar el curso');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchContent();
  }, [user, authLoading, fetchContent, navigate]);

  const updateLessonCompleted = useCallback((lessonId, quizScore = null) => {
    setContent(prev => ({
      ...prev,
      completed_lessons: prev.completed_lessons + (allLessons.find(l => l.id === lessonId)?.is_completed ? 0 : 1),
      modules: prev.modules.map(m => ({
        ...m,
        lessons: m.lessons.map(l =>
          l.id === lessonId ? { ...l, is_completed: true, ...(quizScore !== null ? { quiz_score: quizScore } : {}) } : l
        )
      }))
    }));
  }, [allLessons]);

  const handleComplete = async () => {
    if (!activeLesson || activeLesson.is_completed) {
      if (nextLesson) setActiveLessonId(nextLesson.id);
      return;
    }
    setCompleting(true);
    try {
      const res = await completeLesson({ course_id: id, lesson_id: activeLesson.id });
      updateLessonCompleted(activeLesson.id);
      setContent(prev => ({ ...prev, progress: res.data.progress }));
      toast.success('¡Lección completada!');
      if (nextLesson) setActiveLessonId(nextLesson.id);
    } catch {
      toast.error('Error al completar la lección');
    } finally {
      setCompleting(false);
    }
  };

  const handleQuizComplete = (score, progress) => {
    updateLessonCompleted(activeLesson.id, score);
    if (progress !== undefined) setContent(prev => ({ ...prev, progress: progress ?? prev.progress }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!content) return null;

  return (
    <div className="flex h-full overflow-hidden bg-background" data-testid="course-learn-page">
      {/* Sidebar overlay on mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <CourseSidebar
        content={content}
        activeLessonId={activeLessonId}
        onSelect={setActiveLessonId}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center gap-3 px-4 py-3 border-b bg-background shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 rounded hover:bg-muted"
            data-testid="sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-sm truncate">{content.course.title}</h1>
            {activeLesson && (
              <p className="text-xs text-muted-foreground truncate">{activeLesson.title}</p>
            )}
          </div>
          {!content.is_instructor && (
            <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground shrink-0">
              <Progress value={content.progress} className="w-24 h-1.5" />
              <span>{content.progress}%</span>
            </div>
          )}
        </header>

        {/* Lesson content */}
        <div className="flex-1 overflow-y-auto">
          {activeLesson ? (
            <div className="max-w-3xl mx-auto px-4 py-8" data-testid="lesson-content">
              {/* Lesson header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <TypeIcon type={activeLesson.type} size="w-5 h-5" />
                  <Badge variant="outline" className="text-xs capitalize">
                    {activeLesson.type === 'video' ? 'Video' : activeLesson.type === 'text' ? 'Texto' : 'Quiz'}
                  </Badge>
                  {activeLesson.duration_minutes && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />{activeLesson.duration_minutes} min
                    </span>
                  )}
                  {activeLesson.is_completed && (
                    <Badge className="bg-green-100 text-green-700 text-xs gap-1">
                      <CheckCircle className="w-3 h-3" />Completada
                    </Badge>
                  )}
                </div>
                <h2 className="text-xl md:text-2xl font-bold">{activeLesson.title}</h2>
              </div>

              {/* Content body */}
              {activeLesson.type === 'video' && activeLesson.content && (
                <div className="mb-6">
                  <VideoPlayer url={activeLesson.content} />
                </div>
              )}

              {activeLesson.type === 'text' && activeLesson.content && (
                <div
                  className="lesson-content mb-6 bg-muted/20 rounded-xl p-6"
                  dangerouslySetInnerHTML={{ __html: activeLesson.content }}
                  data-testid="lesson-text-content"
                />
              )}

              {activeLesson.type === 'quiz' && (
                <div className="mb-6">
                  <QuizPlayer
                    lesson={activeLesson}
                    courseId={id}
                    onComplete={handleQuizComplete}
                  />
                </div>
              )}

              {/* Actions */}
              {!content.is_instructor && (
                <div className="flex items-center justify-between pt-4 border-t mt-6 gap-3 flex-wrap">
                  <Button
                    variant="outline"
                    disabled={!prevLesson}
                    onClick={() => prevLesson && setActiveLessonId(prevLesson.id)}
                    data-testid="prev-lesson-btn"
                    className="gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </Button>

                  {activeLesson.type !== 'quiz' && (
                    <Button
                      onClick={handleComplete}
                      disabled={completing}
                      data-testid="complete-lesson-btn"
                      className={`gap-2 ${activeLesson.is_completed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      {completing
                        ? 'Guardando...'
                        : activeLesson.is_completed
                        ? 'Siguiente lección'
                        : 'Marcar como completada'}
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    disabled={!nextLesson}
                    onClick={() => nextLesson && setActiveLessonId(nextLesson.id)}
                    data-testid="next-lesson-btn"
                    className="gap-2"
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Course completed banner */}
              {!content.is_instructor && content.progress >= 100 && (
                <div className="mt-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl text-center">
                  <Award className="w-10 h-10 mx-auto mb-2 text-amber-500" />
                  <h3 className="font-bold text-green-800 mb-1">¡Curso completado!</h3>
                  <p className="text-sm text-green-700 mb-3">Felicitaciones por terminar el curso.</p>
                  <Link to="/dashboard">
                    <Button size="sm" className="gap-2">
                      <Award className="w-4 h-4" />
                      Ver mis certificados
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
              <BookOpen className="w-12 h-12 mb-4 opacity-30" />
              <p className="font-medium">Selecciona una lección para comenzar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseLearn;
